import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, SlidersHorizontal, X } from 'lucide-react';

interface MarketplaceFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  dealTypeFilter: string;
  onDealTypeChange: (value: string) => void;
  valueRangeFilter: string;
  onValueRangeChange: (value: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const MarketplaceFilters: React.FC<MarketplaceFiltersProps> = ({
  searchTerm,
  onSearchChange,
  dealTypeFilter,
  onDealTypeChange,
  valueRangeFilter,
  onValueRangeChange,
  sortBy,
  onSortChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  return (
    <div className="space-y-4 p-4 bg-muted/10 rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-7 px-2 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Deal Type Filter */}
        <Select value={dealTypeFilter} onValueChange={onDealTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="All Deal Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Deal Types</SelectItem>
            <SelectItem value="sponsorship">Sponsorship</SelectItem>
            <SelectItem value="endorsement">Endorsement</SelectItem>
            <SelectItem value="partnership">Partnership</SelectItem>
            <SelectItem value="appearance">Appearance</SelectItem>
          </SelectContent>
        </Select>

        {/* Value Range Filter */}
        <Select value={valueRangeFilter} onValueChange={onValueRangeChange}>
          <SelectTrigger>
            <SelectValue placeholder="All Values" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Values</SelectItem>
            <SelectItem value="0-5000">$0 - $5,000</SelectItem>
            <SelectItem value="5000-10000">$5,000 - $10,000</SelectItem>
            <SelectItem value="10000-20000">$10,000 - $20,000</SelectItem>
            <SelectItem value="20000+">$20,000+</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort By */}
        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger>
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="value-high">Highest Value</SelectItem>
            <SelectItem value="value-low">Lowest Value</SelectItem>
            <SelectItem value="deadline">Deadline</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};